/**
 * Requests API Service
 * Handles analysis request creation via POST /api/Requests
 * and request status polling via GET /api/Requests/requestStatus
 */

import { apiClient } from './client';
import { env } from '@/utils/env';
import type { AnalysisFormData } from '@/components/analysis/AnalysisDialog';

/**
 * Payload parameter for the request
 */
export interface PayloadParameter {
  parameterName: string;
  payload: string;
}

/**
 * Request payload for creating a new analysis request
 */
export interface CreateRequestPayload {
  productId: number;
  payloadParameters: PayloadParameter[];
}

/**
 * Response from creating a request (adjust based on actual API response)
 */
export interface CreateRequestResponse {
  id: number;
  productId: number;
  status: number;
  createdAt: string;
  correlationId: string;
}

/**
 * Request status values
 */
export enum RequestStatus {
  Pending = 0,
  InProgress = 1,
  Completed = 2,
  Failed = 3,
}

/**
 * Feature result from the analysis (raw API format)
 */
export interface FeatureResultRaw {
  featureId?: number;
  featureTemplateId?: number;
  featureName: string;
  featureDescription?: string;
  /** The result data - can be an array of objects or a string */
  result?: unknown[];
  value?: string;
  status: number;
  /** Feature progress percentage (0-100) */
  progressPercent?: number;
  /** Total number of prompts for this feature */
  totalPrompts?: number;
  /** Number of completed prompts for this feature */
  completedPrompts?: number;
  executionStartedAt?: string;
  executionFinishedAt?: string;
}

/**
 * Normalized feature result for use in components
 */
export interface FeatureResult {
  featureId: number;
  featureName: string;
  featureDescription?: string;
  /** The result value as a JSON string */
  value: string;
  status: number;
  /** Feature progress percentage (0-100) */
  progressPercent: number;
  /** Total number of prompts for this feature */
  totalPrompts: number;
  /** Number of completed prompts for this feature */
  completedPrompts: number;
}

/**
 * Raw API response - features can be an object or array
 */
export interface RequestStatusResponseRaw {
  correlationId: string;
  /** Product ID that determines the analysis type/variant */
  productId?: number;
  status: RequestStatus;
  /** Overall progress percentage (0-100) */
  progressPercent?: number;
  createdAt?: string;
  finishedAt?: string;
  lastErrorMessage?: string | null;
  /** Whether this request has already been retried by the user */
  hasBeenRetried?: boolean;
  /** Features can be an object (keyed by name) or an array */
  features: Record<string, FeatureResultRaw> | FeatureResultRaw[];
}

/**
 * Normalized response from request status endpoint
 */
export interface RequestStatusResponse {
  correlationId: string;
  /** Product ID that determines the analysis type/variant */
  productId: number;
  status: RequestStatus;
  /** Overall progress percentage (0-100) */
  progressPercent: number;
  createdAt?: string;
  finishedAt?: string;
  lastErrorMessage?: string | null;
  /** Whether this request has already been retried by the user */
  hasBeenRetried: boolean;
  features: FeatureResult[];
}

/**
 * Normalize a raw feature result to the expected format
 */
function normalizeFeatureResult(raw: FeatureResultRaw): FeatureResult {
  // Convert result array to JSON string if present
  let value = raw.value || '';
  if (raw.result && Array.isArray(raw.result)) {
    value = JSON.stringify(raw.result);
  }

  return {
    featureId: raw.featureId ?? raw.featureTemplateId ?? 0,
    featureName: raw.featureName,
    featureDescription: raw.featureDescription,
    value,
    status: raw.status,
    progressPercent: raw.progressPercent ?? 0,
    totalPrompts: raw.totalPrompts ?? 0,
    completedPrompts: raw.completedPrompts ?? 0,
  };
}

/**
 * Normalize raw API response to expected format
 */
function normalizeRequestStatusResponse(raw: RequestStatusResponseRaw): RequestStatusResponse {
  let features: FeatureResult[] = [];

  if (raw.features) {
    if (Array.isArray(raw.features)) {
      // Features is already an array
      features = raw.features.map(normalizeFeatureResult);
    } else if (typeof raw.features === 'object') {
      // Features is an object keyed by feature name
      features = Object.values(raw.features).map(normalizeFeatureResult);
    }
  }

  return {
    correlationId: raw.correlationId,
    productId: raw.productId ?? 0,
    status: raw.status,
    progressPercent: raw.progressPercent ?? 0,
    createdAt: raw.createdAt,
    finishedAt: raw.finishedAt,
    lastErrorMessage: raw.lastErrorMessage,
    hasBeenRetried: raw.hasBeenRetried ?? false,
    features,
  };
}

/**
 * Convert form data to API payload parameters
 */
function formDataToPayloadParameters(formData: AnalysisFormData): PayloadParameter[] {
  const paramNames = env.analysis.parameterNames;

  const params: PayloadParameter[] = [
    {
      parameterName: paramNames.vertriebsgebiet,
      payload: formData.vertriebsgebiet,
    },
    {
      parameterName: paramNames.zielgruppe,
      payload: formData.zielgruppe === 'andere'
        ? (formData.zielgruppeCustom ?? '')
        : formData.zielgruppe,
    },
    {
      parameterName: paramNames.branche,
      payload: formData.branche,
    },
    {
      parameterName: paramNames.vertriebsziel,
      payload: formData.vertriebsziel,
    },
    {
      parameterName: paramNames.produktlink,
      payload: formData.produktlink,
    },
  ];

  if (formData.vertriebszielDetails?.trim()) {
    params.push({
      parameterName: paramNames.vertriebszielDetails,
      payload: formData.vertriebszielDetails.trim(),
    });
  }

  return params;
}

/**
 * Response from feature result endpoint
 */
export interface FeatureResultResponse {
  featureTemplateId: number;
  featureName: string;
  /** The result data from AI processing */
  result: unknown;
}

/**
 * Requests API service
 */
export const requestsService = {
  /**
   * Create a new analysis request
   */
  async createRequest(formData: AnalysisFormData): Promise<CreateRequestResponse> {
    const payload: CreateRequestPayload = {
      productId: env.analysis.getProductId(formData.zielgruppe),
      payloadParameters: formDataToPayloadParameters(formData),
    };

    return apiClient.post<CreateRequestResponse>('/api/Requests', payload);
  },

  /**
   * Get request status by correlation ID
   */
  async getRequestStatus(correlationId: string): Promise<RequestStatusResponse> {
    const raw = await apiClient.get<RequestStatusResponseRaw>(
      `/api/Requests/requestStatus?correlationId=${encodeURIComponent(correlationId)}`
    );
    return normalizeRequestStatusResponse(raw);
  },

  /**
   * Retry a failed request (only failed prompts are resent, once per request)
   */
  async retryFailedRequest(correlationId: string): Promise<{ message: string }> {
    return apiClient.post<{ message: string }>(
      `/api/Requests/user-retry-failed?correlationId=${encodeURIComponent(correlationId)}`,
      {}
    );
  },

  /**
   * Get feature result by correlation ID and feature name
   * Only call this when feature status is 2 (completed)
   */
  async getFeatureResult(correlationId: string, featureName: string): Promise<FeatureResultResponse> {
    const params = new URLSearchParams({ correlationId, featureName });
    return apiClient.get<FeatureResultResponse>(
      `/api/Requests/featureResult?${params.toString()}`
    );
  },
};
